import React from "react";
import {useTranslation} from "react-i18next";
import {
  ICartContextNoCart,
  isCart,
  useCartContext,
  useUnsafeCartContext,
} from "../CartContextProvider/CartContextProvider";
import * as Sentry from "@sentry/browser";
import * as ReactDOM from "react-dom";

interface IAddToCartButtonProps {
  itemId: number;
  showQuantity?: boolean;
  styleClasses?: string;
}

const buttonAttributes = (styleClassess: string | undefined) => ({
  className: `button product-cart__addbutton${!!styleClassess ? ` ${styleClassess}` : ""}`,
});

const AddToCartButtonPortal: React.FC<{targetNode: HTMLSpanElement | null; root: HTMLSpanElement | null}> = (props) => {
  const {t} = useTranslation();
  const context = useUnsafeCartContext();
  const {targetNode, root} = props;

  React.useEffect(() => {
    if (!root || !targetNode) {
      return;
    }

    const problems = targetNode.getElementsByClassName("problem");
    if (problems.length) {
      root.appendChild(problems[0]);
    }
    targetNode.replaceWith(root);
  }, []);

  if (!root || !targetNode) {
    return null;
  }

  // if this is not there the item is most likely "nicht mehr lieferbar"
  const buttonPlaceholder = targetNode.querySelector<HTMLElement>(".product-cart-placeholder");

  if (!buttonPlaceholder || !buttonPlaceholder.dataset.itemid) {
    return null;
  }

  const itemId = parseInt(buttonPlaceholder.dataset.itemid, 10);

  // TODO this is not the best way to go, because there is a slight chance that the button will be clicked
  //   while the first request to check for a visitor cart is still not back. But for now this will do.
  if (!context) {
    return <button {...buttonAttributes("button-large")}>{t("In den Warenkorb")}</button>;
  }

  return ReactDOM.createPortal(
    <React.Fragment>
      {isCart(context) ? (
        <AddToCartButton itemId={itemId} styleClasses="button-large" />
      ) : (
        <AddToCartButtonFallback context={context} itemId={itemId} styleClasses="button-large" />
      )}
    </React.Fragment>,
    root,
  );
};

export const AddToCartButtonFallback: React.FC<{context: ICartContextNoCart; itemId: number; styleClasses?: string}> = (props) => {
  const {t} = useTranslation("cart");
  const {requestCart, setItemsAdded} = props.context;
  const requestCartAndAddItem = () => {
    requestCart().then((result) => {
      const {cart, done} = result;
      cart.addItem(props.itemId, 1).then(() => {
        setItemsAdded([props.itemId]);
        done();
      }, Sentry.captureException);
    }, Sentry.captureException);
  };

  return (
    <button {...buttonAttributes(props.styleClasses ?? "button-large")} onClick={requestCartAndAddItem}>
      {t("In den Warenkorb")}
    </button>
  );
};

export const AddToCartButton: React.FC<IAddToCartButtonProps> = ({itemId, showQuantity = true, styleClasses = "button-large"}) => {
  const context = useCartContext();
  const {cart} = context;
  const {t} = useTranslation("cart");

  const position = cart.positions.find(
    (pos) => pos.type === "Item" && !!pos.reference?.itemId && pos.item?.itemId === itemId,
  );
  const quantity = position?.quantity ?? 0;

  const addToCart = async () => {
    await cart.addItem(itemId, quantity + 1);
    context.setItemsAdded([itemId]);
  };

  return (
    <React.Fragment>
      <button {...buttonAttributes(styleClasses)} onClick={addToCart}>
        {t("In den Warenkorb") + (quantity > 0 && showQuantity ? ` (${quantity})` : "")}
      </button>
    </React.Fragment>
  );
};

export default AddToCartButtonPortal;
